import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';

/**
 * Generic single-field edit prompt — for any little "edit one string"
 * UX where a `window.prompt` would be the lazy fallback. Pass a label,
 * the current value, an onSave callback, and optionally a list of
 * preset suggestions (rendered as clickable chips above the input).
 */
export interface EditFieldPromptData {
   label: string;
   currentValue: string;
   onSave: (newValue: string) => void;
   placeholder?: string;
   hint?: string;
   /** Optional click-to-fill chips above the input (e.g., common emojis). */
   presets?: string[];
   /** Defaults to "Save". */
   submitLabel?: string;
}

interface Props {
   data: EditFieldPromptData;
}

const EditFieldPrompt = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const [value, setValue] = useState(data.currentValue);
   const inputRef = useRef<HTMLInputElement>(null);

   useEffect(() => {
      // Focus + select on open so admin can start typing immediately.
      const t = setTimeout(() => {
         inputRef.current?.focus();
         inputRef.current?.select();
      }, 0);
      return () => clearTimeout(t);
   }, []);

   const submit = () => {
      data.onSave(value);
      handleClose();
   };

   return (
      <Wrapper>
         <Label>{data.label}</Label>
         {data.hint && <Hint>{data.hint}</Hint>}

         {data.presets && data.presets.length > 0 && (
            <PresetGrid>
               {data.presets.map((p) => (
                  <PresetChip
                     key={p}
                     type="button"
                     $active={value === p}
                     onClick={() => setValue(p)}
                  >
                     {p}
                  </PresetChip>
               ))}
            </PresetGrid>
         )}

         <TextInput
            ref={inputRef}
            value={value}
            placeholder={data.placeholder}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
               if (e.key === 'Enter') submit();
               if (e.key === 'Escape') handleClose();
            }}
         />

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={submit}>
               <Btn
                  title={data.submitLabel ?? 'Save'}
                  type="normal"
                  size="small"
                  bgcolor={colors.PRIMARY}
                  textcolor={colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 380px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 10px;
   @media (max-width: 480px) { width: 100%; }
`;

const Label = styled.label`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const PresetGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
   gap: 6px;
`;

const PresetChip = styled.button<{ $active: boolean }>`
   font-size: 20px;
   padding: 6px 8px;
   border-radius: 8px;
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}15` : colors.WHITE)};
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   cursor: pointer;
   line-height: 1;
   text-align: center;
   font-family: inherit;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const TextInput = styled.input`
   padding: 9px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 14px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

export default EditFieldPrompt;
